<template>
  <div style="white-space: nowrap">
    <b-overlay :show="isLoading || isModalLoading" class="d-flex" style="gap: 12px">
      <!-- Seitenleiste -->
      <div>
        <div class="card p-2" style="gap: 12px; align-items: center; width: 100%">
          <div
            v-for="(saison, i) in metadata"
            :key="'saison-' + saison.id"
            class="p-3 rounded"
            style="cursor: pointer; width: 100%"
            :class="activeSaisonGueltigAb === saison.gueltigAb ? 'bg-primary text-white' : 'bg-secondary'"
            @click="setActiveSaisonGueltigAb(saison.gueltigAb)"
          >
            <div class="d-flex">
              {{ `${i + 1}. Saison` }}
            </div>
            <div>{{ 'Ab ' + saison.formattedGueltigAb }}</div>
            <div class="d-flex">
              <span>{{ saison.reisetermineOfThisSeason?.length }} RT</span>
              <a
                :class="activeSaisonGueltigAb === saison.gueltigAb ? 'bg-primary text-white' : 'bg-secondary'"
                class="ml-auto px-1"
                :href="
                  '/#/reisetermine/planung?reisetermine=' +
                  saison.reisetermineOfThisSeason.map(item => item.reiseterminkuerzel).join(',')
                "
                target="_blank"
              >
                <b-icon icon="box-arrow-up-right"></b-icon>
              </a>
            </div>
          </div>
          <div class="w-100" style="display: grid; grid-template-columns: repeat(5, 1fr)">
            <b-button
              class="btn-xs btn py-1 px-2 justify-content-center align-items-center d-flex"
              v-b-tooltip.hover
              :title="'Die Reisestammdaten werden pro Reise und pro Saison angelegt und gepflegt. Bei Anlage eines neuen Reisetermins werden die Daten der jeweiligen Saison, in der der Termin liegt, automatisch übernommen.'"
            >
              <i class="fas fa-info m-0 p-0"></i>
            </b-button>
            <div style="grid-column-start: 3" class="bg-primary plus-sign" v-b-modal.modalSaisonHinzufuegen>
              +
            </div>
          </div>
        </div>
      </div>
      <!-- Show active season -->
      <div class="d-flex w-100" style="flex-direction: column; position: relative">
        <div class="card card-body p-4" style="grid-column: span 3">
          <span
            class="d-flex ml-auto"
            style="
              gap: 12px;
              width: fit-content;
              position: sticky;
              top: 130px;
              z-index: 99;
              background-color: var(--surface);
              margin-bottom: -38px;
            "
          >
            <div
              class="icon-button d-inline-flex justify-content-center align-middle"
              style="width: 36px"
              @click="createTaskWithData"
              v-b-modal.modalAufgabeEdit
              v-b-tooltip.hover.bottom
              :title="'Aufgabe erstellen'"
            >
              <span class="d-flex align-items-center"><ListChecks :size="20" /></span>
            </div>
            <b-button
              size="sm"
              class="save-btn dialog-btn"
              @click="saveChanges"
              :disabled="disableSichernButton"
              :variant="disableSichernButton ? '' : 'primary'"
            >
              Speichern
            </b-button>
            <b-button
              size="sm"
              class="dialog-btn-secondary"
              v-b-modal.modalSaisonLöschen
              variant="secondary"
              v-if="activeMetadata?.gueltigAb?.substring(0, 10) !== '0001-01-01'"
            >
              Saison löschen
            </b-button>
          </span>
          <div class="d-flex" style="gap: 8px">
            <b-button
              v-for="tab in tabs"
              :key="tab.text"
              size="sm"
              :class="activeTab === tab.text ? 'dialog-btn' : 'dialog-btn-secondary'"
              :variant="activeTab === tab.text ? 'primary' : 'secondary'"
              @click="activeTab = tab.text"
            >
              <div v-if="tab.show">
                {{ tab.text }}
              </div>
            </b-button>
          </div>
          <template v-if="activeTab === 'Allgemein'">
            <ReisenMetadataCardAllgemein
              :editedSeason="editedSeason"
              :changedFields="changedFields"
              :activeMetadata="activeMetadata"
            />
            <ReisenMetadataCardPlanZimmerkontingente
              :editedSeason="editedSeason"
              :activeMetadata="activeMetadata"
              :reise="reise"
              :activeSaisonGueltigAb="activeSaisonGueltigAb"
              @showNotUpdatedZimmerkontingente="onShowNotUpdatedZimmerkontingente"
            />
            <ReisenMetadataCardReleaseinformationen
              :editedSeason="editedSeason"
              :activeMetadata="activeMetadata"
            />
            <ReisenMetadataCardTransfer
              :editedSeason="editedSeason"
              :changedFields="changedFields"
              :reise="reise"
            />
            <ReisenMetadataCardFlugdaten
              :editedSeason="editedSeason"
              :changedFields="changedFields"
              :reise="reise"
            />
            <ReisenMetadataCardInlandsfluege
              :editedSeason="editedSeason"
              :activeMetadata="activeMetadata"
              :reise="reise"
              :activeSaisonGueltigAb="activeSaisonGueltigAb"
              @showNotUpdatedInlandsfluege="onShowNotUpdatedInlandsfluege"
            />
          </template>
          <div v-if="activeTab === 'Verlauf'">
            <DataChangeHistory :data="verlaufData"></DataChangeHistory>
          </div>
        </div>
      </div>
    </b-overlay>

    <b-modal id="modalSaisonHinzufuegen" centered title="Saison hinzufügen" size="sm">
      <b-overlay :show="isModalLoading">
        <b-form-group label="Gültig ab">
          <DatePicker :lang="langConfig" type="date" format="DD.MM.YYYY" v-model="gueltigAb"></DatePicker>
        </b-form-group>
      </b-overlay>
      <template #modal-footer="{ hide }" class="pl-1">
        <b-button class="dialog-btn-secondary" @click="resetAndCloseModal(hide)">Abbrechen</b-button>
        <b-button
          class="dialog-btn"
          @click="handleModalSubmit(hide)"
          :disabled="gueltigAb === null"
          variant="primary"
        >
          Erstellen
        </b-button>
      </template>
    </b-modal>
    <DeleteConfirmationModal
      id="modalSaisonLöschen"
      title="Saison löschen?"
      entityBezeichnung="die Saison"
      :label="activeSaisonGueltigAbFormatted"
      :loading="isModalLoading"
      @confirm="deleteSaison"
    />
    <b-modal
      id="modalShowNotUpdatedFields"
      centered
      hide-footer
      title="Die folgenden Daten der Termine aus der Saison wurden nicht verändert"
      size="lg"
      @hide="notUpdatedFields = null"
    >
      <div class="d-flex" style="flex-direction: column" v-if="notUpdatedFields !== null">
        <ul v-for="(value, key) in notUpdatedFields" :key="key">
          <li style="font-size: 14px">{{ key }}: {{ value.join(', ') }}</li>
        </ul>
        <h6 class="mt-4">
          Sieh sie dir
          <a target="_blank" :href="getLinkForReiseterminkuerzel(Object.keys(notUpdatedFields))">hier</a>
          in der Terminplanung an.
        </h6>
      </div>
    </b-modal>
    <b-modal
      id="modalShowNotUpdatedFieldsInlandsfluege"
      centered
      hide-footer
      title="Die folgenden Inlandsflüge der Termine aus der Saison wurden nicht verändert"
      size="lg"
      @hide="reiseterminNotUpdatedInlandsfluege = null"
    >
      <div class="d-flex" style="flex-direction: column">
        <ul v-for="reisetermin in reiseterminNotUpdatedInlandsfluege" :key="reisetermin">
          <li style="font-size: 14px">{{ reisetermin }}</li>
        </ul>
        <h6 class="mt-4">
          Sieh sie dir
          <a target="_blank" :href="getLinkForReiseterminkuerzel(reiseterminNotUpdatedInlandsfluege)">hier</a>
          in der Terminplanung an.
        </h6>
      </div>
    </b-modal>
    <AufgabeErstellenModal :prefixedVerlinkungen="reiseterminVerlinkungen" />
  </div>
</template>

<script>
import { formatDateWithoutHours, langConfig, arraysEqual } from '@/core/common/helpers/utils.js';
import { compareAsc, compareDesc, format, isAfter, isBefore, isSameDay } from 'date-fns';
import DatePicker from 'vue2-datepicker';
import apiService from '@/core/common/services/api.service';
import { SET_ACTIVE_SAISON_GUELTIG_AB } from '@/core/produkte/reisen/stores/reisen-metadata.module.js';
import { mapState } from 'vuex';
import AufgabeErstellenModal from '@/components/aufgaben/aufgabe-erstellen-modal.vue';
import DataChangeHistory from '@/components/produkte/reisen/data-change-history.vue';
import DeleteConfirmationModal from '@/components/common/delete-confirmation-modal.vue';
import ReisenMetadataCardAllgemein from '@/components/produkte/reisen/reisen-metadata/reisen-metadata-card-allgemein.vue';
import ReisenMetadataCardPlanZimmerkontingente from '@/components/produkte/reisen/reisen-metadata/reisen-metadata-card-plan-zimmerkontingente.vue';
import ReisenMetadataCardReleaseinformationen from '@/components/produkte/reisen/reisen-metadata/reisen-metadata-card-releaseinformationen.vue';
import ReisenMetadataCardTransfer from '@/components/produkte/reisen/reisen-metadata/reisen-metadata-card-transfer.vue';
import ReisenMetadataCardFlugdaten from '@/components/produkte/reisen/reisen-metadata/reisen-metadata-card-flugdaten.vue';
import ReisenMetadataCardInlandsfluege from '@/components/produkte/reisen/reisen-metadata/reisen-metadata-card-inlandsfluege.vue';
import { ListChecks } from 'lucide-vue';

export default {
  name: 'ReisenMetadataTable',
  props: {
    reise: { type: Object },
  },
  components: {
    ListChecks,
    DatePicker,
    AufgabeErstellenModal,
    DataChangeHistory,
    DeleteConfirmationModal,
    ReisenMetadataCardAllgemein,
    ReisenMetadataCardPlanZimmerkontingente,
    ReisenMetadataCardReleaseinformationen,
    ReisenMetadataCardTransfer,
    ReisenMetadataCardFlugdaten,
    ReisenMetadataCardInlandsfluege,
  },
  data() {
    return {
      langConfig,
      notUpdatedFields: null,
      reiseterminNotUpdatedInlandsfluege: null,
      isLoading: null,
      isModalLoading: false,
      gueltigAb: null,
      metadata: null,
      lastSaveChangedFields: [],
      reiseterminVerlinkungen: [],
      editedSeason: {
        hotelnaechte: null,
        kalkPax: null,
        durchfuehrbareMinPax: null,
        minPax: null,
        maxPax: null,
        transferszenarioHin: null,
        transferszenarioRueck: null,
        kommentarTransferszenarioHin: null,
        kommentarTransferszenarioRueck: null,
        transferHinBesonderheit: null,
        transferRueckBesonderheit: null,
        abweichenderFlughafenHinIataCode: null,
        abweichenderFlughafenRueckIataCode: null,
        flugoption: null,
        countryManagerKuerzel: null,
        ziellandZielflughaefenIataCodes: null,
        ziellandZielflughafenPrio: null,
        ziellandAbflughaefenIataCodes: null,
        ziellandAbflughafenPrio: null,
        overnightInbound: null,
        overnightOutbound: null,
        fruehesterAbflug: null,
        spaetesterAbflug: null,
        restriktionenHinRollover: null,
        fruehesteAnkunft: null,
        spaetesteAnkunft: null,
        restriktionenRueckRollover: null,
        releasefrist: null,
        skrFlugangebot: null,
        inlandsfluege: null,
        planZimmerkontingente: null,
        hotelstartVorLandung: null,
        rueckflugNachHotelende: null,
        erwarteteTermindurchfuehrungsquote: null,
        einkaufspreisVornachtDzProPerson: null,
        vorverlaengerung: null,
        vornachtMoeglich: null,
        hinflugMaxWartezeit: null,
        hinflugMaxAnzahlTransfergruppen: null,
        isKeineTransferplanungNotwendigHin: null,
        isKeineTransferplanungNotwendigRueck: null,
        maxTransfergruppenBeachtenHin: null,
        maxTransfergruppenBeachtenRueck: null,
        rueckflugMaxWartezeit: null,
        rueckflugMaxAnzahlTransfergruppen: null,
      },
      activeTab: 'Allgemein',
      verlaufData: null,
    };
  },
  mounted() {
    this.prepareData();
  },
  watch: {
    activeSaisonGueltigAb() {
      this.setEditedData();
      this.loadVerlauf(this.activeMetadata?.reiseId, this.activeMetadata?.gueltigAb);
    },
    activeTab() {
      if (this.activeTab === 'Verlauf') {
        this.loadVerlauf(this.activeMetadata?.reiseId, this.activeMetadata?.gueltigAb);
      }
    },
    reise() {
      this.prepareData();
    },
  },
  computed: {
    ...mapState({
      activeSaisonGueltigAb: state => state.reisenMetadata.activeSaisonGueltigAb,
    }),
    activeSaisonGueltigAbFormatted() {
      return formatDateWithoutHours(this.activeSaisonGueltigAb);
    },
    // Zustands- und Speicherlogik (zb zentrale speicherung und computed) bleibt hier in der datei
    // die neuen sektionsfiles greifen dann auf die geteilte editSeason zu
    activeMetadata() {
      if (this.metadata) {
        return this.metadata.find(item => item.gueltigAb === this.activeSaisonGueltigAb);
      }
      return null;
    },
    activeMetadataIndex() {
      if (this.metadata) {
        return this.metadata.findIndex(item => item.gueltigAb === this.activeSaisonGueltigAb);
      }
      return null;
    },
    reisetermineInFutureAndSeason() {
      if (!isNaN(this.activeMetadataIndex) && this.activeMetadataIndex > -1) {
        const nextSaisonId = this.activeMetadataIndex === 0 ? null : this.activeMetadataIndex - 1;
        const nextGueltigAb = nextSaisonId === null ? null : this.metadata[nextSaisonId]?.gueltigAb;
        let reisetermine = this.reise.reisetermine.filter(
          rt =>
            isAfter(new Date(rt.abreisedatum), new Date()) &&
            isAfter(new Date(rt.abreisedatum), new Date(this.activeMetadata?.gueltigAb))
        );
        if (nextGueltigAb) {
          reisetermine = reisetermine.filter(rt =>
            isBefore(new Date(rt.abreisedatum), new Date(nextGueltigAb))
          );
        }
        return reisetermine;
      } else return [];
    },
    oldAbflughafenString() {
      if (this.activeMetadata?.ziellandAbflughaefen) {
        return this.activeMetadata?.ziellandAbflughaefen
          .slice()
          .map(fh => fh.flughafenIataCode)
          .sort((a, b) => a.localeCompare(b))
          .join('');
      } else return '';
    },
    oldZielflughafenString() {
      if (this.activeMetadata?.ziellandZielflughaefen) {
        return this.activeMetadata?.ziellandZielflughaefen
          .slice()
          .map(fh => fh.flughafenIataCode)
          .sort((a, b) => a.localeCompare(b))
          .join('');
      } else return '';
    },
    newZielflughafenString() {
      if (this.editedSeason.ziellandZielflughaefenIataCodes) {
        return this.editedSeason.ziellandZielflughaefenIataCodes
          .slice()
          .sort((a, b) => a.localeCompare(b))
          .join('');
      } else return '';
    },
    newAbflughafenString() {
      if (this.editedSeason.ziellandAbflughaefenIataCodes) {
        return this.editedSeason.ziellandAbflughaefenIataCodes
          .slice()
          .sort((a, b) => a.localeCompare(b))
          .join('');
      } else return '';
    },
    oldCountryManagerString() {
      if (this.activeMetadata?.countryManagers) {
        return this.activeMetadata.countryManagers
          .map(cm => cm.countryManagerKuerzel)
          .sort((a, b) => a.localeCompare(b))
          .join('');
      } else return '';
    },
    newCountryManagerString() {
      if (this.editedSeason.countryManagerKuerzel) {
        return this.editedSeason.countryManagerKuerzel
          .slice()
          .sort((a, b) => a.localeCompare(b))
          .join('');
      } else return '';
    },
    countryManagerKuerzelChanged() {
      return this.newCountryManagerString !== this.oldCountryManagerString;
    },
    changedFields() {
      return [
        this.countryManagerKuerzelChanged ? 'countryManagerKuerzel' : null,
        this.ziellandZielflughafenPrioChanged ? 'ziellandZielflughafenPrio' : null,
        this.ziellandAbflughafenPrioChanged ? 'ziellandAbflughafenPrio' : null,
        this.ziellandZielflughafenChanged ? 'ziellandZielflughaefenIataCodes' : null,
        this.ziellandAbflughafenChanged ? 'ziellandAbflughaefenIataCodes' : null,
        this.hotelnaechteChanged ? 'hotelnaechte' : null,
        this.kalkPaxChanged ? 'kalkPax' : null,
        this.minPaxChanged ? 'minPax' : null,
        this.maxPaxChanged ? 'maxPax' : null,
        this.durchfuehrbareMinPaxChanged ? 'durchfuehrbareMinPax' : null,
        this.vornachtMoeglichChanged ? 'vornachtMoeglich' : null,
        this.transferszenarioHinChanged ? 'transferszenarioHin' : null,
        this.transferszenarioRueckChanged ? 'transferszenarioRueck' : null,
        this.kommentarTransferszenarioHinChanged ? 'kommentarTransferszenarioHin' : null,
        this.kommentarTransferszenarioRueckChanged ? 'kommentarTransferszenarioRueck' : null,
        this.transferHinBesonderheitChanged ? 'transferHinBesonderheit' : null,
        this.transferRueckBesonderheitChanged ? 'transferRueckBesonderheit' : null,
        this.abweichenderFlughafenHinIataCodeChanged ? 'abweichenderFlughafenHinIataCode' : null,
        this.abweichenderFlughafenRueckIataCodeChanged ? 'abweichenderFlughafenRueckIataCode' : null,
        this.flugoptionChanged ? 'flugoption' : null,
        this.skrFlugangebotChanged ? 'skrFlugangebot' : null,
        this.overnightInboundChanged ? 'overnightInbound' : null,
        this.overnightOutboundChanged ? 'overnightOutbound' : null,
        this.hotelstartVorLandungChanged ? 'hotelstartVorLandung' : null,
        this.rueckflugNachHotelendeChanged ? 'rueckflugNachHotelende' : null,
        this.fruehesteAnkunftChanged ? 'fruehesteAnkunft' : null,
        this.spaetesteAnkunftChanged ? 'spaetesteAnkunft' : null,
        this.restriktionenHinRolloverChanged ? 'restriktionenHinRollover' : null,
        this.fruehesterAbflugChanged ? 'fruehesterAbflug' : null,
        this.spaetesterAbflugChanged ? 'spaetesterAbflug' : null,
        this.restriktionenRueckRolloverChanged ? 'restriktionenRueckRollover' : null,
        this.erwarteteTermindurchfuehrungsquoteChanged ? 'erwarteteTermindurchfuehrungsquote' : null,
        this.einkaufspreisVornachtDzProPersonChanged ? 'einkaufspreisVornachtDzProPerson' : null,
        this.releasefristChanged ? 'releasefrist' : null,
        this.vorverlaengerungChanged ? 'vorverlaengerung' : null,
        this.hinflugMaxWartezeitChanged ? 'hinflugMaxWartezeit' : null,
        this.hinflugMaxAnzahlTransfergruppenChanged ? 'hinflugMaxAnzahlTransfergruppen' : null,
        this.isKeineTransferplanungNotwendigHinChanged ? 'isKeineTransferplanungNotwendigHin' : null,
        this.isKeineTransferplanungNotwendigRueckChanged ? 'isKeineTransferplanungNotwendigRueck' : null,
        this.maxTransfergruppenBeachtenHinChanged ? 'maxTransfergruppenBeachtenHin' : null,
        this.maxTransfergruppenBeachtenRueckChanged ? 'maxTransfergruppenBeachtenRueck' : null,
        this.rueckflugMaxWartezeitChanged ? 'rueckflugMaxWartezeit' : null,
        this.rueckflugMaxAnzahlTransfergruppenChanged ? 'rueckflugMaxAnzahlTransfergruppen' : null,
      ].filter(Boolean);
    },
    ziellandZielflughafenPrioChanged() {
      return (
        (this.editedSeason.ziellandZielflughafenPrio ?? null) !==
        (this.activeMetadata?.ziellandZielflughaefen?.find(fh => fh.isPrio)?.flughafenIataCode ?? null)
      );
    },
    ziellandAbflughafenPrioChanged() {
      return (
        (this.editedSeason.ziellandAbflughafenPrio ?? null) !==
        (this.activeMetadata?.ziellandAbflughaefen?.find(fh => fh.isPrio)?.flughafenIataCode ?? null)
      );
    },
    ziellandZielflughafenChanged() {
      return this.newZielflughafenString !== this.oldZielflughafenString;
    },
    ziellandAbflughafenChanged() {
      return this.newAbflughafenString !== this.oldAbflughafenString;
    },
    // Der Plus-Operator wird benötigt, da Formularfelder als Strings gespeichert werden und ich sie mit Zahlen vergleichen möchte
    hotelnaechteChanged() {
      return +this.editedSeason.hotelnaechte !== +this.activeMetadata?.hotelnaechte;
    },
    kalkPaxChanged() {
      return +this.editedSeason.kalkPax !== +this.activeMetadata?.kalkPax;
    },
    minPaxChanged() {
      return +this.editedSeason.minPax !== +this.activeMetadata?.minPax;
    },
    maxPaxChanged() {
      return +this.editedSeason.maxPax !== +this.activeMetadata?.maxPax;
    },
    durchfuehrbareMinPaxChanged() {
      return +this.editedSeason.durchfuehrbareMinPax !== +this.activeMetadata?.durchfuehrbareMinPax;
    },
    vorverlaengerungChanged() {
      return this.editedSeason?.vorverlaengerung !== this.activeMetadata?.vorverlaengerung;
    },
    vornachtMoeglichChanged() {
      return Boolean(this.editedSeason.vornachtMoeglich) !== Boolean(this.activeMetadata?.vornachtMoeglich);
    },
    hotelstartVorLandungChanged() {
      return (
        Boolean(this.editedSeason.hotelstartVorLandung) !== Boolean(this.activeMetadata?.hotelstartVorLandung)
      );
    },
    rueckflugNachHotelendeChanged() {
      return (
        Boolean(this.editedSeason.rueckflugNachHotelende) !==
        Boolean(this.activeMetadata?.rueckflugNachHotelende)
      );
    },
    restriktionenHinRolloverChanged() {
      return (
        Boolean(this.editedSeason.restriktionenHinRollover) !==
        Boolean(this.activeMetadata?.restriktionenHinRollover)
      );
    },
    restriktionenRueckRolloverChanged() {
      return (
        Boolean(this.editedSeason.restriktionenRueckRollover) !==
        Boolean(this.activeMetadata?.restriktionenRueckRollover)
      );
    },
    transferszenarioHinChanged() {
      return this.editedSeason.transferszenarioHin !== this.activeMetadata?.transferszenarioHin;
    },
    transferszenarioRueckChanged() {
      return this.editedSeason.transferszenarioRueck !== this.activeMetadata?.transferszenarioRueck;
    },
    kommentarTransferszenarioHinChanged() {
      return (
        this.editedSeason.kommentarTransferszenarioHin !== this.activeMetadata?.kommentarTransferszenarioHin
      );
    },
    kommentarTransferszenarioRueckChanged() {
      return (
        this.editedSeason.kommentarTransferszenarioRueck !==
        this.activeMetadata?.kommentarTransferszenarioRueck
      );
    },
    transferHinBesonderheitChanged() {
      return this.editedSeason.transferHinBesonderheit != this.activeMetadata?.transferHinBesonderheit;
    },
    transferRueckBesonderheitChanged() {
      return this.editedSeason.transferRueckBesonderheit != this.activeMetadata?.transferRueckBesonderheit;
    },
    abweichenderFlughafenHinIataCodeChanged() {
      return (
        (this.editedSeason.abweichenderFlughafenHinIataCode ?? null) !==
        (this.activeMetadata?.abweichenderFlughafenHinIataCode ?? null)
      );
    },
    abweichenderFlughafenRueckIataCodeChanged() {
      return (
        (this.editedSeason.abweichenderFlughafenRueckIataCode ?? null) !==
        (this.activeMetadata?.abweichenderFlughafenRueckIataCode ?? null)
      );
    },

    releasefristChanged() {
      return !this.arraysEqual(
        (this.editedSeason.releasefrist ?? []).map(item => item.value),
        this.activeMetadata?.releasefrist
      );
    },
    flugoptionChanged() {
      return this.editedSeason.flugoption !== (this.activeMetadata?.flugoption ?? 'ReiseMitFlug');
    },
    skrFlugangebotChanged() {
      return this.editedSeason.skrFlugangebot !== this.activeMetadata?.skrFlugangebot;
    },
    overnightInboundChanged() {
      return this.editedSeason.overnightInbound !== this.activeMetadata?.overnightInbound;
    },
    overnightOutboundChanged() {
      return this.editedSeason.overnightOutbound !== this.activeMetadata?.overnightOutbound;
    },
    fruehesteAnkunftChanged() {
      return (
        this.editedSeason.fruehesteAnkunft?.substring(0, 5) !==
        this.activeMetadata?.fruehesteAnkunft?.substring(0, 5)
      );
    },
    spaetesteAnkunftChanged() {
      return (
        this.editedSeason.spaetesteAnkunft?.substring(0, 5) !==
        this.activeMetadata?.spaetesteAnkunft?.substring(0, 5)
      );
    },
    fruehesterAbflugChanged() {
      return (
        this.editedSeason.fruehesterAbflug?.substring(0, 5) !==
        this.activeMetadata?.fruehesterAbflug?.substring(0, 5)
      );
    },
    spaetesterAbflugChanged() {
      return (
        this.editedSeason.spaetesterAbflug?.substring(0, 5) !==
        this.activeMetadata?.spaetesterAbflug?.substring(0, 5)
      );
    },
    erwarteteTermindurchfuehrungsquoteChanged() {
      return (
        this.editedSeason.erwarteteTermindurchfuehrungsquote !==
        this.activeMetadata?.erwarteteTermindurchfuehrungsquote
      );
    },
    einkaufspreisVornachtDzProPersonChanged() {
      return (
        +this.editedSeason.einkaufspreisVornachtDzProPerson !==
        +this.activeMetadata?.einkaufspreisVornachtDzProPerson
      );
    },
    hinflugMaxWartezeitChanged() {
      return +this.editedSeason.hinflugMaxWartezeit !== +this.activeMetadata?.hinflugMaxWartezeit;
    },
    hinflugMaxAnzahlTransfergruppenChanged() {
      return (
        +this.editedSeason.hinflugMaxAnzahlTransfergruppen !==
        +this.activeMetadata?.hinflugMaxAnzahlTransfergruppen
      );
    },
    isKeineTransferplanungNotwendigHinChanged() {
      return (
        Boolean(this.editedSeason.isKeineTransferplanungNotwendigHin) !==
        Boolean(this.activeMetadata?.isKeineTransferplanungNotwendigHin)
      );
    },
    isKeineTransferplanungNotwendigRueckChanged() {
      return (
        Boolean(this.editedSeason.isKeineTransferplanungNotwendigRueck) !==
        Boolean(this.activeMetadata?.isKeineTransferplanungNotwendigRueck)
      );
    },
    maxTransfergruppenBeachtenHinChanged() {
      return (
        Boolean(this.editedSeason.maxTransfergruppenBeachtenHin) !==
        Boolean(this.activeMetadata?.maxTransfergruppenBeachtenHin)
      );
    },
    maxTransfergruppenBeachtenRueckChanged() {
      return (
        Boolean(this.editedSeason.maxTransfergruppenBeachtenRueck) !==
        Boolean(this.activeMetadata?.maxTransfergruppenBeachtenRueck)
      );
    },
    rueckflugMaxWartezeitChanged() {
      return +this.editedSeason.rueckflugMaxWartezeit !== +this.activeMetadata?.rueckflugMaxWartezeit;
    },
    rueckflugMaxAnzahlTransfergruppenChanged() {
      return (
        +this.editedSeason.rueckflugMaxAnzahlTransfergruppen !==
        +this.activeMetadata?.rueckflugMaxAnzahlTransfergruppen
      );
    },
    disableSichernButton() {
      return (
        !this.countryManagerKuerzelChanged &&
        !this.ziellandZielflughafenPrioChanged &&
        !this.ziellandAbflughafenPrioChanged &&
        !this.ziellandZielflughafenChanged &&
        !this.ziellandAbflughafenChanged &&
        !this.hotelnaechteChanged &&
        !this.kalkPaxChanged &&
        !this.minPaxChanged &&
        !this.maxPaxChanged &&
        !this.durchfuehrbareMinPaxChanged &&
        !this.vornachtMoeglichChanged &&
        !this.transferszenarioHinChanged &&
        !this.transferszenarioRueckChanged &&
        !this.kommentarTransferszenarioHinChanged &&
        !this.kommentarTransferszenarioRueckChanged &&
        !this.transferHinBesonderheitChanged &&
        !this.transferRueckBesonderheitChanged &&
        !this.abweichenderFlughafenHinIataCodeChanged &&
        !this.abweichenderFlughafenRueckIataCodeChanged &&
        !this.flugoptionChanged &&
        !this.skrFlugangebotChanged &&
        !this.overnightInboundChanged &&
        !this.overnightOutboundChanged &&
        !this.hotelstartVorLandungChanged &&
        !this.rueckflugNachHotelendeChanged &&
        !this.fruehesteAnkunftChanged &&
        !this.spaetesteAnkunftChanged &&
        !this.restriktionenHinRolloverChanged &&
        !this.fruehesterAbflugChanged &&
        !this.spaetesterAbflugChanged &&
        !this.restriktionenRueckRolloverChanged &&
        !this.erwarteteTermindurchfuehrungsquoteChanged &&
        !this.einkaufspreisVornachtDzProPersonChanged &&
        !this.releasefristChanged &&
        !this.vorverlaengerungChanged &&
        !this.hinflugMaxWartezeitChanged &&
        !this.hinflugMaxAnzahlTransfergruppenChanged &&
        !this.isKeineTransferplanungNotwendigHinChanged &&
        !this.isKeineTransferplanungNotwendigRueckChanged &&
        !this.maxTransfergruppenBeachtenHinChanged &&
        !this.maxTransfergruppenBeachtenRueckChanged &&
        !this.rueckflugMaxWartezeitChanged &&
        !this.rueckflugMaxAnzahlTransfergruppenChanged
      );
    },
    tabs() {
      return [
        { show: true, text: 'Allgemein' },
        { show: true, text: 'Verlauf' },
      ];
    },
  },
  methods: {
    createTaskWithData() {
      this.$bvModal.show('modalAufgabeEdit');
      this.reiseterminVerlinkungen = this.activeMetadata.reisetermineOfThisSeason.map((item, i) => ({
        id: i,
        typ: 'Reisetermin',
        artifactId: item.id,
        artifactText: item.reiseterminkuerzel,
      }));
    },
    prepareData() {
      this.metadata = this.reise.fullMetadataHistory
        .map(item => {
          const reisetermineOfThisSeason = this.getReiseterminKuerzelInSeason(item.gueltigAb);
          return {
            ...item,
            formattedGueltigAb: formatDateWithoutHours(item.gueltigAb),
            reisetermineOfThisSeason: reisetermineOfThisSeason.map(item => ({
              id: item.id,
              reiseterminkuerzel: item.reiseterminkuerzel,
            })),
          };
        })
        .sort((a, b) => compareDesc(new Date(a.gueltigAb), new Date(b.gueltigAb)));

      if (this.$route.query.reiseterminAbreisedatum) {
        const { reiseterminAbreisedatum } = this.$route.query;
        const filteredReisemetadataGueltigAb = this.reise.fullMetadataHistory
          .slice()
          .sort((a, b) => compareDesc(new Date(a.gueltigAb), new Date(b.gueltigAb)))
          .map(item => item.gueltigAb)
          .filter(item => new Date(item) < new Date(reiseterminAbreisedatum));
        this.setActiveSaisonGueltigAb(filteredReisemetadataGueltigAb[0]);
      }
      if (this.activeSaisonGueltigAb === null) {
        this.setActiveSaisonGueltigAb(this.metadata[0].gueltigAb);
      }
      this.setEditedData();
    },
    arraysEqual,
    getReiseterminKuerzelInSeason(lowerBound) {
      const sortedReisemetadataGueltigAb = this.reise.fullMetadataHistory
        .slice()
        .sort((a, b) => compareAsc(new Date(a.gueltigAb), new Date(b.gueltigAb)))
        .map(item => item.gueltigAb);
      const index = sortedReisemetadataGueltigAb.indexOf(lowerBound);

      if (index === sortedReisemetadataGueltigAb.length - 1) {
        return this.reise.reisetermine.filter(rt => new Date(rt.abreisedatum) >= new Date(lowerBound));
      }

      const upperBound = sortedReisemetadataGueltigAb[index + 1];
      return this.reise.reisetermine.filter(
        rt =>
          new Date(rt.abreisedatum) >= new Date(lowerBound) &&
          new Date(rt.abreisedatum) < new Date(upperBound)
      );
    },
    getLinkForReiseterminkuerzel(reiseterminkuerzels) {
      if (reiseterminkuerzels !== null) {
        return '/#/reisetermine/planung?reisetermine=' + reiseterminkuerzels;
      } else return '';
    },
    setActiveSaisonGueltigAb(i) {
      this.$store.commit(SET_ACTIVE_SAISON_GUELTIG_AB, i);
    },
    handleModalSubmit(hide) {
      if (this.gueltigAb) {
        const metadataToCopy = this.metadata.find(item => item.gueltigAb === this.activeSaisonGueltigAb);
        const reisekuerzel = this.reise.reisekuerzel;
        const gueltigAbNewFormatted = format(new Date(this.gueltigAb), 'yyyy-MM-dd');
        const gueltigAbOldFormatted = format(new Date(metadataToCopy.gueltigAb), 'yyyy-MM-dd');
        this.isModalLoading = true;
        apiService
          .post(
            `Reise/${reisekuerzel}/metadata/${gueltigAbOldFormatted}/copyMetadata?gueltigAbNew=${gueltigAbNewFormatted}`
          )
          .then(response => {
            const result = response.data.result.value;
            this.$emit('reiseUpdated');
            this.toast('Erfolgreich erstellt.');

            this.setActiveSaisonGueltigAb(gueltigAbNewFormatted + 'T00:00:00Z');
            this.notUpdatedFields = result.item1;

            if (Object.keys(this.notUpdatedFields).length > 0) {
              this.$root.$emit('bv::show::modal', 'modalShowNotUpdatedFields');
            }
            this.gueltigAb = null;
            this.isModalLoading = false;
            hide();
          })
          .catch(() => {
            setTimeout(() => {
              this.isModalLoading = false;
              this.gueltigAb = null;
            }, 1500);
          });
      }
    },
    setEditedData() {
      if (this.activeMetadata) {
        this.editedSeason = {
          hotelnaechte: this.activeMetadata.hotelnaechte,
          kalkPax: this.activeMetadata.kalkPax,
          minPax: this.activeMetadata.minPax,
          maxPax: this.activeMetadata.maxPax,
          durchfuehrbareMinPax: this.activeMetadata.durchfuehrbareMinPax,
          vornachtMoeglich: this.activeMetadata.vornachtMoeglich,
          transferszenarioHin: this.activeMetadata.transferszenarioHin,
          transferszenarioRueck: this.activeMetadata.transferszenarioRueck,
          kommentarTransferszenarioHin: this.activeMetadata.kommentarTransferszenarioHin,
          kommentarTransferszenarioRueck: this.activeMetadata.kommentarTransferszenarioRueck,
          transferHinBesonderheit: this.activeMetadata.transferHinBesonderheit,
          transferRueckBesonderheit: this.activeMetadata.transferRueckBesonderheit,
          abweichenderFlughafenHinIataCode: this.activeMetadata.abweichenderFlughafenHinIataCode ?? null,
          abweichenderFlughafenRueckIataCode: this.activeMetadata.abweichenderFlughafenRueckIataCode ?? null,
          flugoption: this.activeMetadata.flugoption ?? 'ReiseMitFlug',
          countryManagerKuerzel:
            this.activeMetadata.countryManagers?.map(cm => cm.countryManagerKuerzel) ?? [],
          ziellandAbflughaefenIataCodes: this.activeMetadata.ziellandAbflughaefen.map(
            fh => fh.flughafenIataCode
          ),
          ziellandZielflughaefenIataCodes: this.activeMetadata.ziellandZielflughaefen.map(
            fh => fh.flughafenIataCode
          ),
          ziellandAbflughafenPrio: this.activeMetadata.ziellandAbflughaefen.find(fh => fh.isPrio)
            ?.flughafenIataCode,
          ziellandZielflughafenPrio: this.activeMetadata.ziellandZielflughaefen.find(fh => fh.isPrio)
            ?.flughafenIataCode,
          overnightInbound: this.activeMetadata.overnightInbound,
          overnightOutbound: this.activeMetadata.overnightOutbound,
          // only take hours and seconds for consistency, because datepicker uses that format aswell
          fruehesteAnkunft: this.activeMetadata.fruehesteAnkunft?.substring(0, 5) ?? null,
          spaetesteAnkunft: this.activeMetadata.spaetesteAnkunft?.substring(0, 5) ?? null,
          restriktionenHinRollover: Boolean(this.activeMetadata.restriktionenHinRollover),
          fruehesterAbflug: this.activeMetadata.fruehesterAbflug?.substring(0, 5) ?? null,
          spaetesterAbflug: this.activeMetadata.spaetesterAbflug?.substring(0, 5) ?? null,
          restriktionenRueckRollover: Boolean(this.activeMetadata.restriktionenRueckRollover),
          releasefrist: this.activeMetadata.releasefrist.map((item, i) => ({ value: item, id: i + 1 })),
          skrFlugangebot: this.activeMetadata.skrFlugangebot,
          hotelstartVorLandung: this.activeMetadata.hotelstartVorLandung,
          rueckflugNachHotelende: this.activeMetadata.rueckflugNachHotelende,
          erwarteteTermindurchfuehrungsquote: this.activeMetadata.erwarteteTermindurchfuehrungsquote,
          einkaufspreisVornachtDzProPerson: this.activeMetadata.einkaufspreisVornachtDzProPerson,
          inlandsfluege: this.activeMetadata.inlandsfluege.map(inlandsflug => ({
            ...inlandsflug,
            spaetesteAnkunft: inlandsflug.spaetesteAnkunft?.substring(0, 5) ?? null,
            fruehesterAbflug: inlandsflug.fruehesterAbflug?.substring(0, 5) ?? null,
          })),
          // flache Kopie der geladenen Zimmerkontingente, damit das Original unverändert bleibt
          planZimmerkontingente: (this.activeMetadata.planZimmerkontingente ?? []).map(zimmerkontingent => ({
            ...zimmerkontingent,
          })),
          vorverlaengerung: this.activeMetadata.vorverlaengerung,
          hinflugMaxWartezeit: this.activeMetadata.hinflugMaxWartezeit,
          hinflugMaxAnzahlTransfergruppen: this.activeMetadata.hinflugMaxAnzahlTransfergruppen,
          isKeineTransferplanungNotwendigHin: this.activeMetadata.isKeineTransferplanungNotwendigHin,
          isKeineTransferplanungNotwendigRueck: this.activeMetadata.isKeineTransferplanungNotwendigRueck,
          maxTransfergruppenBeachtenHin: this.activeMetadata.maxTransfergruppenBeachtenHin,
          maxTransfergruppenBeachtenRueck: this.activeMetadata.maxTransfergruppenBeachtenRueck,
          rueckflugMaxWartezeit: this.activeMetadata.rueckflugMaxWartezeit,
          rueckflugMaxAnzahlTransfergruppen: this.activeMetadata.rueckflugMaxAnzahlTransfergruppen,
        };
      }
    },
    saveChanges() {
      this.isLoading = true;
      const reisekuerzel = this.reise.reisekuerzel;
      this.lastSaveChangedFields = this.changedFields;
      const timeOnlyParams = ['fruehesteAnkunft', 'spaetesteAnkunft', 'fruehesterAbflug', 'spaetesterAbflug'];
      const filterOutKeys = ['inlandsfluege', 'planZimmerkontingente'];
      let params = Object.keys(this.editedSeason)
        .filter(item => !filterOutKeys.includes(item))
        .reduce((acc, curr) => {
          if (curr === 'releasefrist') {
            acc[curr] = this.editedSeason.releasefrist.map(item => item.value);
          } else if (curr === 'restriktionenHinRollover' || curr === 'restriktionenRueckRollover') {
            acc[curr] = this.editedSeason[curr] == null ? null : +this.editedSeason[curr];
          } else if (timeOnlyParams.includes(curr) && this.editedSeason[curr]) {
            // add seconds so that .net accepts this value as TimeOnly
            acc[curr] = this.editedSeason[curr] + ':00';
          } else {
            acc[curr] = this.editedSeason[curr];
          }
          return acc;
        }, {});
      if (
        Object.keys(params).includes('ziellandZielflughaefenIataCodes') &&
        !Object.keys(params).includes('ziellandZielflughafenPrio')
      ) {
        params['ziellandZielflughafenPrio'] = this.editedSeason['ziellandZielflughafenPrio'];
      }
      if (
        Object.keys(params).includes('ziellandAbflughaefenIataCodes') &&
        !Object.keys(params).includes('ziellandAbflughafenPrio')
      ) {
        params['ziellandAbflughafenPrio'] = this.editedSeason['ziellandAbflughafenPrio'];
      }

      apiService
        .put(`Reise/${reisekuerzel}/metadataAndApplyToReisetermin/${this.activeSaisonGueltigAb}`, params)
        .then(response => {
          const result = response.data.result.value;
          const newMetadata = response.data.result.value.item2;
          this.toast('Erfolgreich bearbeitet.');
          const specialFields = [
            'ziellandAbflughaefenIataCodes',
            'ziellandZielflughaefenIataCodes',
            'ziellandAbflughafenPrio',
            'ziellandZielflughafenPrio',
          ];

          // only show not updated fields of changed fields
          const onlyChangedFields = Object.entries(result.item1).reduce((acc, curr) => {
            const reiseterminkuerzel = curr[0];
            const notUpdatedFields = curr[1];
            const lowerCaseLastSaveChangedFields = this.lastSaveChangedFields
              .map(field => field.toLowerCase())
              .map(field => {
                if (field === 'ziellandzielflughaefeniatacodes') {
                  return 'ziellandzielflughaefen';
                } else if (field === 'ziellandabflughaefeniatacodes') {
                  return 'ziellandabflughaefen';
                } else {
                  return field;
                }
              });

            const notChangedFields = notUpdatedFields.filter(key =>
              lowerCaseLastSaveChangedFields.includes(key.toLowerCase())
            );
            if (notChangedFields.length > 0) {
              acc[reiseterminkuerzel] = notChangedFields;
            }
            return acc;
          }, {});

          this.notUpdatedFields = onlyChangedFields;
          this.isLoading = false;

          if (
            this.reisetermineInFutureAndSeason?.length > 0 &&
            Object.keys(this.notUpdatedFields).length > 0
          ) {
            this.$root.$emit('bv::show::modal', 'modalShowNotUpdatedFields');
          }
          this.metadata = this.metadata.map(m => {
            if (m.gueltigAb.substring(0, 10) === this.activeSaisonGueltigAb?.substring(0, 10)) {
              for (const field of this.lastSaveChangedFields) {
                if (specialFields.includes(field)) {
                  m['ziellandZielflughaefen'] = newMetadata.ziellandZielflughaefenIataCodes.map(fh => ({
                    flughafenIataCode: fh,
                    isPrio: fh === newMetadata.ziellandZielflughafenPrio,
                  }));
                  m['ziellandAbflughaefen'] = newMetadata.ziellandAbflughaefenIataCodes.map(fh => ({
                    flughafenIataCode: fh,
                    isPrio: fh === newMetadata.ziellandAbflughafenPrio,
                  }));
                } else if (field === 'countryManagerKuerzel') {
                  m['countryManagers'] = (newMetadata.countryManagerKuerzel ?? []).map(kuerzel => ({
                    countryManagerKuerzel: kuerzel,
                  }));
                } else {
                  m[field] = newMetadata[field];
                }
              }
              m['vkVornachtDzProPerson'] = newMetadata['vkVornachtDzProPerson'];
              return m;
            } else return m;
          });
        })
        .catch(() => {
          this.setEditedData();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    async deleteSaison() {
      if (!isSameDay(new Date(this.activeMetadata.gueltigAb), new Date('0001-01-01'))) {
        const reisekuerzel = this.reise.reisekuerzel;
        const gueltigAb = this.activeMetadata?.gueltigAb;
        this.isModalLoading = true;
        apiService.delete(`Reise/${reisekuerzel}/metadata/${gueltigAb}`).then(response => {
          const result = response.data.result.value;
          this.$emit('reiseUpdated');
          this.toast('Erfolgreich gelöscht.');
          this.setActiveSaisonGueltigAb(null);
          this.notUpdatedFields = result;

          if (Object.keys(this.notUpdatedFields).length > 0) {
            this.$root.$emit('bv::show::modal', 'modalShowNotUpdatedFields');
          }

          this.isModalLoading = false;
          this.$bvModal.hide('modalSaisonLöschen');
        });
      }
    },
    resetAndCloseModal(hide) {
      this.gueltigAb = null;
      hide();
    },
    // Zeigt im "nicht verändert"-Popup an, für welche Reisetermine das geänderte Zimmerkontingent nicht übernommen wurde
    onShowNotUpdatedZimmerkontingente(reiseterminkuerzels) {
      this.notUpdatedFields = reiseterminkuerzels.reduce((acc, kuerzel) => {
        acc[kuerzel] = ['Zimmerkontingente'];
        return acc;
      }, {});
      this.$root.$emit('bv::show::modal', 'modalShowNotUpdatedFields');
    },
    onShowNotUpdatedInlandsfluege(reiseterminNotUpdatedInlandsfluege) {
      this.reiseterminNotUpdatedInlandsfluege = reiseterminNotUpdatedInlandsfluege;
      if (this.reiseterminNotUpdatedInlandsfluege?.length > 0) {
        this.$root.$emit('bv::show::modal', 'modalShowNotUpdatedFieldsInlandsfluege');
      }
    },
    loadVerlauf(reiseId, gueltigAb) {
      if (reiseId && gueltigAb) {
        apiService.get(`/history/ReiseMetadata/${reiseId}/${gueltigAb}`).then(response => {
          if (response.status === 200) {
            this.verlaufData = response.data.result;
          }
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.save-btn:disabled {
  cursor: default;
  pointer-events: all;
  opacity: 1;
  background-color: #e4e6ef;
  border-color: #e4e6ef;
  color: var(--text-subtle);
}

.plus-sign {
  height: 24px;
  width: 24px;
  border-radius: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  color: white;
  cursor: pointer;
  -webkit-user-select: none;
  /* Safari */
  -ms-user-select: none;
  /* IE 10 and IE 11 */
  user-select: none;
  /* Standard syntax */
}

:deep(.mx-datepicker-content) {
  margin: 0 !important;
}
</style>
